<template>
  <div>
    <tab-list :tabs="tabs">
      <template #default="{ activeTab }">
        <section id="comments" class="section" tabindex="0" :hidden="activeTab !== 0">
          <div class="row">
            <div class="columns small-12 medium-8 large-8">
              <h3>{{ commentCount }} {{ pluralise('Comment', commentCount) }}</h3>

              <p>
                Write a review to help other teachers and parents like yourself. If you'd like to
                request a change to this resource, or report an error, select the corresponding tab
                above.
              </p>

              <comment-form :resource-id="resourceId" type="comment" layout="minimal" />
              <comment-list
                v-if="commentCount > 0"
                :resource-id="resourceId"
                :show-load-more-button="$isFlagOn('show-more-comments-btn')"
                :event-params="eventParams"
              />
            </div>
          </div>
        </section>
        <section id="requests" class="section" tabindex="0" :hidden="activeTab !== 1">
          <div class="row">
            <div class="columns small-12 medium-8 large-8">
              <template v-if="resourceUpdates && resourceUpdates.length > 0">
                <h3>Resource updates</h3>

                <ul class="no-list">
                  <comment-item
                    v-for="update in resourceUpdates"
                    :key="update.id"
                    :comment="update"
                  />
                </ul>

                <hr />
              </template>

              <h3>Suggest a Change</h3>

              <p>
                Would you like something changed or customised on this resource? While our team
                makes every effort to complete change suggestions, we can't guarantee that every
                change will be completed.
              </p>

              <nuxt-link
                v-if="isAuthenticated"
                :to="localePath('/suggest-a-change/')"
                class="button button-primary"
                title="Go to form to submit a change"
                @click.native="trackAnalytics('suggest_change')"
              >
                Suggest a change
              </nuxt-link>
              <div v-else class="callout">
                <span>
                  <nuxt-link :to="localePath('/login/')" nofollow title="Log in to your account">
                    <span @click.prevent="toggleLoginModal(true)">Login</span>
                  </nuxt-link>
                  to suggest a change
                </span>
              </div>
            </div>
          </div>
        </section>
        <section id="errors" class="section" tabindex="0" :hidden="activeTab !== 2">
          <div class="row">
            <div class="columns small-12 medium-8 large-6">
              <h3>Report an Error</h3>

              <p>
                Did you spot an error on this resource? Please let us know and we will fix it
                shortly.
              </p>

              <nuxt-link
                v-if="isAuthenticated"
                :to="localePath('/report-an-error/')"
                class="button button-primary"
                title="Go to form to report an error"
                @click.native="trackAnalytics('report_error')"
              >
                Report an error
              </nuxt-link>
              <div v-else class="callout">
                <span>
                  <nuxt-link :to="localePath('/login/')" nofollow title="Log in to your account">
                    <span @click.prevent="toggleLoginModal(true)">Login</span>
                  </nuxt-link>
                  to report an error
                </span>
              </div>
            </div>
          </div>
        </section>
        <section id="section-help" class="section" tabindex="0" :hidden="activeTab !== 3">
          <div class="row">
            <div class="columns small-12 medium-8 large-8">
              <h3>Help</h3>

              <p>
                Are you having trouble downloading or viewing this resource? Please try the
                following steps:
              </p>

              <ol>
                <li>Check that you are logged in to your account</li>
                <li>
                  For premium resources, check that you have a
                  <a href="/plans/" title="View subscription plans">paid subscription</a>
                </li>
                <li>
                  Check that you have installed Adobe Reader (
                  <a
                    href="https://get.adobe.com/reader/"
                    title="Download Adobe PDF reader"
                    target="_blank"
                    rel="noopener"
                  >
                    download here
                  </a>
                  )
                </li>
              </ol>

              <p>
                If you are still having difficulty, please visit the
                <a
                  href="https://support.teachstarter.com/"
                  title="Visit Teach Starter support"
                  target="_blank"
                  rel="noopener"
                >
                  Teach Starter Help Desk
                </a>
                or
                <a
                  href="https://support.teachstarter.com/support/solutions/articles/51000349243-teach-starter-support"
                  title="View Teach Starter contact details"
                  target="_blank"
                  rel="noopener"
                >
                  contact us
                </a>
                .
              </p>
            </div>
          </div>
        </section>
      </template>
    </tab-list>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

import CommentForm from './CommentForm.vue';
import TabList from '~/components/TabList.vue';
import { pluralise } from '~/utils/formatStrings';

export default {
  name: 'CommentSection',
  components: {
    TabList,
    CommentList: () => import('./CommentList.vue'),
    CommentItem: () => import('./CommentItem.vue'),
    CommentForm,
  },
  props: {
    commentCount: { type: Number, default: 0 },
    resourceId: { type: Number, default: null },
    resourceUpdates: {
      type: Array,
      default: null,
    },
    eventParams: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      resource: this.$store.getters.resource,
      tabs: [
        { name: 'Comments', slug: 'comments', active: true },
        { name: 'Suggest a Change', slug: 'changes' },
        { name: 'Report an Error', slug: 'errors' },
        { name: 'Help', slug: 'help' },
      ],
    };
  },
  computed: {
    ...mapGetters(['isAuthenticated']),
  },
  methods: {
    pluralise,
    changeTab(id, name = '') {
      this.activeTab = id;
      const tab = name ? '#' + name : '';
      history.pushState({}, null, this.$route.path + tab);
    },
    toggleLoginModal(show) {
      this.$store.dispatch('toggleLoginModal', show);
    },
    trackAnalytics(action) {
      const pagePath = this.$route.path;
      const pathSegments = pagePath.split('/').filter(Boolean);
      let pageType = pathSegments.length > 1 ? pathSegments[1] : pathSegments[0]; // Adjust based on URL structure
      pageType = pageType.replace(/[\s-]+/g, '_').toLowerCase();
      let productAreas = '';
      if (pageType === 'teaching_resource') {
        pageType = 'resource';
        productAreas = 'resources';
      } else if (pageType === 'unit_plan') {
        productAreas = 'unit_plan';
      } else if (pageType === 'teaching_resource_pack') {
        pageType = 'pack';
        productAreas = 'pack';
      } else if (pageType === 'widget') {
        productAreas = 'widget';
      }
      let eventName = '';
      switch (action) {
        case 'suggest_change':
          eventName = `${pageType}.suggest_a_change`;
          break;
        case 'report_error':
          eventName = `${pageType}.report_an_error`;
          break;
      }
      this.$mzAnalyticsApi({
        event: 'click',
        name: eventName,
        productArea: productAreas,
        entityType: pageType,
        entityId: this.resource.id,
      });
    },
  },
};
</script>
<style scoped>
.section {
  padding: 0;
}
</style>
